import LoadingDataGridRowOverlay from '@/components/table/DataGridComponents/LoadingDataGridRowOverlay';
import { NoRowsOverlay } from '../../table/DataGridComponents/NoRowsOverlay';
import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { HitsTablePagination } from '@/components/table/TablePaginationActions';
import { getPaginationItems, MAX_TABLE_SELECTION } from '@/components/table/data';
import { setSelected as setDealSelected } from '@/features/table/dealsTableSlice';
import { setSelected as setTaskSelected } from '@/features/table/tasksTableSlice';
import { useNavigation } from 'react-router-dom';
import Box from '@mui/material/Box';
import { useHits, useInstantSearch } from 'react-instantsearch';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { getComparator, stableSort } from '@/components/table/functions';
import { useBatchGetDealsTasksQuery } from '@/features/engagements/engagementsApi';
import { useBatchGetDealListPoliciesQuery } from '@/features/deals/dealsApi';
import { DataGrid, gridClasses, useGridApiRef } from '@mui/x-data-grid';
import {
	getSearchRecordNavigateOptions,
	getSearchRecordPath,
	mapColumns,
} from './data';
import {
	ColumnControlContextProvider,
	useDataGridColumnProps,
} from '@/context/columnControlContext.jsx';
import { TableSortContextProvider, useTableSort } from '@/context/tableSortContext';
import InstantSearchSortSync from './InstantSearchSortSync';
import { getReferrerEmail, useGetReferralPartnerClientCounts } from '@/hooks/useGetReferralPartnerClientCounts';
import { isIgnoredRowClickTarget, openInNewTab, resolveAppHref } from '@/utils/navigation';
import { useNavigateFromClick } from '@/hooks/useNavigateFromClick';
import { usePipelineContext } from '@/context/pipelineContext';
import InstantSearchDataGridToolbar from './InstantSearchDataGridToolbar';
import LinkedGridRow from '@/components/tables/InstantSearchGridRow';
import DataGridColumnMenu from '../../table/DataGridComponents/DataGridColumnMenu';
import { usesDealRowCache } from '@/utils/pipelineDealSync';
import GlobalSearchHelper from '@/components/tables/InstantSearchDataGrid/GlobalSearchHelper';
import { useTheme } from '@emotion/react';
import { buildRowCacheSnapshot, mergeCachedRow } from '@/utils/rowCacheRegistry';

const EMPTY_SELECTED_ROWS = [];

const initialState = {
	title: '',
	rows: [],
	columns: [],
	indexName: '',
	tab: '',
	objectType: '',
	variant: '',
	rowsPerPage: 5,
	onRowsPerPageChange: () => {},
	includePolicies: false,
	includeTransactions: false,
	hoveredRowId: null,
	setHoveredRowId: (id) => {},
	enableExport: true,
	showPreview: true,
	enableRowClick: true,
	checkboxSelection: false,
	onRowClick: null,
	enableColumnReorder: false,
};

const InstantSearchDataGridContext = createContext(initialState);

export const useInstantSearchDataGridContext = () => {
	const context = useContext(InstantSearchDataGridContext);
	if (!context) {
		throw new Error(
			'useInstantSearchDataGridContext must be used within an InstantSearchDataGrid',
		);
	}
	return context;
};

const InstantSearchDataGrid = ({
	columns,
	rowsPerPage,
	onRowsPerPageChange,
	includeTasks = false,
	includePolicies = false,
	includeTransactions = false,
	objectType, // client, deal, etc.
	variant, // unique variant for column visibility e.g. 'prospect', 'client', pipelineId
	tab, // active tab (if applicable)
	enableExport = true, // add ability to hide Export button
	showPreview = true, // add ability to hide Preview button
	enableRowClick = true,
	checkboxSelection = false,
	onRowClick: onRowClickProp = null,
	defaultHiddenColumns = [],
	showBorder = true,
	enableColumnReorder = false,
	...props
}) => {
	const theme = useTheme();
	const { items: hits } = useHits(props);
	const store = useStore();

	const { uiState } = useInstantSearch(props);
	const indexName = Object.keys(uiState)?.[0];

	const [searchItems, setSearchItems] = useState([]);
	const [hoveredRowId, setHoveredRowId] = useState(null);

	const { pipelines } = usePipelineContext();

	const title = useMemo(() => {
		const varName = variant?.toString()?.replaceAll('/', '');
		const pipelineName = variant ? pipelines?.find((p) => p?.id === variant)?.label : null;
		return `${pipelineName ?? varName ?? indexName} ${tab ?? ''}`.trim();
	}, [indexName, pipelines, variant, tab]);

	const merged = useMemo(() => {
		const merged = [...hits, ...searchItems];
		return merged
			.filter((item, index, self) => index === self.findIndex((t) => t.id === item.id))
			.map((item) => ({ ...item, indexName }));
	}, [searchItems, hits]);

	const items = useMemo(
		() => {
			let deduped = merged.slice();

			const sortBy = uiState?.[indexName]?.sortBy;
			// console.log('Sort by', sortBy);
			let orderBy = null;
			let order = 'asc';

			if (sortBy?.includes('/sort/')) {
				const sortPart = sortBy.split('/sort/')[1];
				const [field, direction] = sortPart.split(':');
				orderBy = field;
				order = direction ?? 'asc';
			}

			if (orderBy && searchItems.length > 0) {
				deduped = stableSort(deduped, getComparator(order, orderBy, true));
			}
			if (rowsPerPage) {
				deduped = deduped.slice(0, rowsPerPage);
			}

			// console.log('Deduped hits', deduped);
			return deduped;
		},
		[merged, rowsPerPage, searchItems, uiState?.[indexName]?.sortBy],
		indexName,
	);

	const itemIds = items.map((h) => h.id);

	// Serialised picked fields so updatedHits recomputes when a cached row changes.
	const rowCacheSnapshot = useSelector((state) =>
		buildRowCacheSnapshot(state, objectType, itemIds),
	);

	const tasksQuery = useBatchGetDealsTasksQuery(
		{ dealIds: itemIds, nextTaskOnly: true },
		{ skip: itemIds.length === 0 || !includeTasks || !objectType },
	);

	const policiesQuery = useBatchGetDealListPoliciesQuery(
		{ dealIds: itemIds, includeTransactions },
		{ skip: itemIds.length === 0 || !usesDealRowCache(objectType) || !includePolicies },
	);

	const { referralCounts, referralCountsLoading } = useGetReferralPartnerClientCounts(
		objectType === 'relationshipContact' ? items : undefined,
	);

	const updatedHits = useMemo(() => {
		const state = store.getState();
		const policyData = policiesQuery.data ?? {};

		const newItems = items.map((hit) => {
			const mergedHit = mergeCachedRow(state, objectType, hit, hit.id);

			const earliestTask = (tasksQuery.data ?? {})[hit.id];

			const policies = policyData?.policiesByDealId?.[mergedHit.dealId] ?? [];
			const policyStatus = policyData?.statusByDealId?.[mergedHit.dealId];

			const policiesLoading =
				policiesQuery.isUninitialized || !policyStatus || policyStatus === 'loading';

			const referrerEmail = getReferrerEmail(mergedHit);

			return {
				...mergedHit,
				docId: mergedHit.id,
				earliestTask,
				nextTaskDate: mergedHit.nextTaskDate,
				policies: !includePolicies ? [] : policies,
				...(includePolicies && {
					policiesLoading,
				}),
				...(objectType === 'relationshipContact' && {
					referralCount: referrerEmail ? (referralCounts[referrerEmail] ?? 0) : null,
					referralCountLoading: Boolean(referrerEmail && referralCountsLoading),
				}),
				uniqueId: mergedHit.id,
			};
		});
		return newItems;
	}, [
		items,
		objectType,
		rowCacheSnapshot,
		store,
		tasksQuery.data,
		policiesQuery.data,
		policiesQuery.isUninitialized,
		policiesQuery.isLoading,
		includePolicies,
		referralCounts,
		referralCountsLoading,
	]);

	const updatedColumns = useMemo(
		() => columns.map((column) => mapColumns({ column, objectType })),
		[columns, objectType],
	);

	const contextValue = useMemo(
		() => ({
			rows: updatedHits,
			columns: updatedColumns,
			indexName,
			tab,
			objectType,
			title,
			variant,
			rowsPerPage,
			onRowsPerPageChange,
			includePolicies,
			includeTransactions,
			hoveredRowId,
			setHoveredRowId,
			enableExport,
			showPreview,
			enableRowClick,
			checkboxSelection,
			onRowClick: onRowClickProp,
			enableColumnReorder,
		}),
		[
			updatedHits,
			updatedColumns,
			indexName,
			tab,
			objectType,
			rowsPerPage,
			onRowsPerPageChange,
			includePolicies,
			includeTransactions,
			title,
			variant,
			hoveredRowId,
			setHoveredRowId,
			enableExport,
			showPreview,
			enableRowClick,
			checkboxSelection,
			onRowClickProp,
			enableColumnReorder,
		],
	);

	return (
		<Box
			{...(showBorder && {
				sx: {
					border: `1px solid ${theme.palette.divider}`,
					borderRadius: '8px',
					backgroundColor: theme.palette.background.paper,
					'& .MuiDataGrid-root': {
						borderRadius: '8px',
					},
				},
			})}
		>
			<InstantSearchDataGridContext.Provider value={contextValue}>
				<GlobalSearchHelper {...{ setSearchItems }} />
				<ColumnControlContextProvider
					value={variant ?? objectType}
					tab={tab}
					variant={objectType}
					defaultHiddenColumns={defaultHiddenColumns}
				>
					<TableSortContextProvider
						value={variant ?? objectType}
						tab={tab}
						variant={objectType}
					>
						<InstantSearchSortSync
							indexName={indexName}
							tab={tab}
							columns={updatedColumns}
						/>
						<InstantSearchDataGridWrapper {...{ slotProps: props?.slotProps }} />
					</TableSortContextProvider>
				</ColumnControlContextProvider>
			</InstantSearchDataGridContext.Provider>
		</Box>
	);
};

InstantSearchDataGrid.displayName = 'InstantSearchDataGrid';

/** Stable slot components — recreating these on render remounts InstantSearch widgets and resets page. */
const InstantSearchDataGridPagination = () => {
	const { rowsPerPage, onRowsPerPageChange } = useInstantSearchDataGridContext();
	const paginationItems = useMemo(() => getPaginationItems(rowsPerPage), [rowsPerPage]);

	return (
		<HitsTablePagination
			rowsPerPage={rowsPerPage}
			onRowsPerPageChange={onRowsPerPageChange}
			items={paginationItems}
		/>
	);
};

InstantSearchDataGridPagination.displayName = 'InstantSearchDataGridPagination';

const InstantSearchDataGridNoRowsOverlay = () => {
	const { objectType } = useInstantSearchDataGridContext();
	return <NoRowsOverlay objectType={objectType} />;
};

InstantSearchDataGridNoRowsOverlay.displayName = 'InstantSearchDataGridNoRowsOverlay';

const InstantSearchDataGridWrapper = ({ slotProps }) => {
	const {
		rows,
		columns,
		objectType,
		hoveredRowId,
		setHoveredRowId,
		indexName,
		enableRowClick,
		checkboxSelection,
		onRowClick: customOnRowClick,
		enableColumnReorder,
	} = useInstantSearchDataGridContext();

	const theme = useTheme();
	const dispatch = useDispatch();
	const { uiState } = useInstantSearch();
	const tableKey = objectType === 'tasks' ? 'taskTable' : 'dealTable';
	const selected = useSelector((state) =>
		checkboxSelection
			? (state[tableKey]?.selected ?? EMPTY_SELECTED_ROWS)
			: EMPTY_SELECTED_ROWS,
	);
	const setSelected = objectType === 'tasks' ? setTaskSelected : setDealSelected;
	const skipInitialFilterClear = useRef(true);
	const appliedFilters = uiState?.[indexName]?.configure?.filters;

	useEffect(() => {
		if (!checkboxSelection) {
			return;
		}

		if (skipInitialFilterClear.current) {
			skipInitialFilterClear.current = false;
			return;
		}

		dispatch(setSelected([]));
	}, [appliedFilters, checkboxSelection, dispatch, setSelected]);

	const { box, dataGrid } = slotProps ?? {};

	const apiRef = useGridApiRef();

	const navigateFromClick = useNavigateFromClick();
	const navigation = useNavigation();

	const { sortModel, setSortModel } = useTableSort();

	const {
		columns: gridColumns,
		columnVisibilityModel,
		onColumnVisibilityModelChange: handleColumnVisibilityModelChange,
		onColumnWidthChange: handleColumnWidthChange,
	} = useDataGridColumnProps(columns, {
		disableColumnOrder: !enableColumnReorder,
	});

	const elementRef = useRef(null);

	const boxSx = useMemo(
		() => ({
			position: 'relative',
			height: objectType === 'client' ? '75vh' : '80vh',
			...(box ?? {}),
		}),
		[objectType, box],
	);

	const isFirstOfRow = (row) =>
		!rows.find(
			(r) =>
				r.id === row.id &&
				r.uniqueId !== row.uniqueId &&
				rows.indexOf(r) < rows.indexOf(row),
		);

	const loadingItem = rows.find((item) => navigation?.location?.pathname?.includes(item.id));

	const navigationLoading = navigation.state == 'loading';

	const loadingRowElement = useMemo(() => {
		if (navigationLoading && loadingItem) {
			const loadingRows = document.querySelectorAll(`.row-item-id-${loadingItem.id}`);
			return loadingRows?.[loadingRows?.length - 1];
		}
	}, [loadingItem, navigationLoading]);

	const handleRowClick = useCallback(
		(params, event) => {
			if (!enableRowClick || isIgnoredRowClickTarget(event)) {
				return;
			}

			if (customOnRowClick) {
				customOnRowClick(params, event);
				return;
			}

			const path = getSearchRecordPath(params.row);
			if (!path) return;

			navigateFromClick(event, path, getSearchRecordNavigateOptions(params.row));
		},
		[customOnRowClick, enableRowClick, navigateFromClick],
	);
	const handleRowAuxClick = useCallback(
		(event) => {
			if (!enableRowClick || customOnRowClick || event.button !== 1) {
				return;
			}

			if (isIgnoredRowClickTarget(event)) {
				return;
			}

			event.preventDefault();
			const rowId = event.currentTarget.getAttribute('data-id');
			const row = apiRef.current?.getRow(rowId);
			const path = getSearchRecordPath(row);
			if (path) {
				openInNewTab(resolveAppHref(path, window.location.pathname));
			}
		},
		[apiRef, customOnRowClick, enableRowClick],
	);

	const rowSelectionModel = useMemo(
		() => selected.map((row) => row.uniqueId ?? row.id).filter(Boolean),
		[selected],
	);

	const handleRowSelectionModelChange = useCallback(
		(model) => {
			const ids = Array.isArray(model) ? model : Array.from(model?.ids ?? []);
			const rowsById = new Map(rows.map((row) => [row.uniqueId, row]));
			const selectedById = new Map(selected.map((row) => [row.uniqueId ?? row.id, row]));
			const nextSelected = ids
				.map((id) => rowsById.get(id) ?? selectedById.get(id))
				.filter(Boolean);

			dispatch(
				setSelected(
					MAX_TABLE_SELECTION != null
						? nextSelected.slice(0, MAX_TABLE_SELECTION)
						: nextSelected,
				),
			);
		},
		[dispatch, rows, selected, setSelected],
	);

	const isRowSelectable = useCallback(
		({ row }) => {
			if (MAX_TABLE_SELECTION == null) return true;
			const isSelected = selected.some((item) => (item.uniqueId ?? item.id) === row.uniqueId);
			return isSelected || selected.length < MAX_TABLE_SELECTION;
		},
		[selected],
	);

	const dataGridSx = useMemo(
		() => ({
			'& .MuiDataGrid-row': {
				position: 'relative',
			},
			'& .row-link-overlay': {
				position: 'absolute',
				inset: 0,
				zIndex: 1,
			},
			'& .MuiDataGrid-cellCheckbox, & .MuiDataGrid-cell[data-field="actions"], & .MuiDataGrid-cell[data-field="status"], & .MuiDataGrid-cell[data-field="automationEnabled"]':
				{
					position: 'relative',
					zIndex: 2,
				},
			'& .MuiDataGrid-cell a, & .MuiDataGrid-cell button, & .MuiDataGrid-cell .MuiSwitch-root, & .MuiDataGrid-cell .MuiCheckbox-root, & .MuiDataGrid-cell [role="button"], & .MuiDataGrid-cell .has-link':
				{
					position: 'relative',
					zIndex: 2,
				},
			'& .MuiDataGrid-editInputCell': {
				backgroundColor: 'transparent',
			},
			'--DataGrid-overlayHeight': '3em',
			'& .MuiDataGrid-cell': {
				padding: '8px 10px',
				borderTop: 'none !important',
			},
			'& .MuiDataGrid-row:hover': {
				backgroundColor: 'transparent',
			},
			'& .first-row-of-item': {
				borderTop: '1px solid var(--DataGrid-rowBorderColor) !important',
			},
			border: 'none',
			[`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
				outline: 'none',
			},
			[`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]: {
				outline: 'none',
			},
			[`& .${gridClasses.columnHeader}`]: {
				borderRight: 'none !important',
			},

			'& .hovered-item-row': {
				backgroundColor: 'action.hover',
			},

			'& .hovered-item-row:hover': {
				backgroundColor: 'action.hover',
				...(enableRowClick && { cursor: 'pointer' }),
			},

			'& .MuiDataGrid-cell[data-field="actions"]': {
				padding: 0,
			},

			'& .hovered-item-row .MuiDataGrid-cell[data-field="actions"] .actions-cell': {
				backgroundColor: 'action.hover',
			},

			'& .hovered-item-row:hover .MuiDataGrid-cell[data-field="actions"] .actions-cell': {
				backgroundColor: 'action.hover',
			},

			'& .file-migration-in-progress': {
				opacity: 0.6,
				cursor: 'default',
				backgroundColor: theme.palette.action.disabledBackground,
				'&:hover': {
					backgroundColor: theme.palette.action.disabledBackground,
					cursor: 'default',
				},
			},
			...(indexName === 'tasks' && {
				[`& .${gridClasses.cell}[data-field="status"]`]: {
					padding: 0,
				},
			}),
			...(dataGrid?.sx ?? {}),
		}),
		[indexName, enableRowClick, dataGrid?.sx, theme],
	);

	return (
		<Box sx={boxSx}>
			<DataGrid
				apiRef={apiRef}
				ref={elementRef}
				sx={dataGridSx}
				getRowId={(row) => row.uniqueId}
				getRowClassName={(params) => {
					const { row } = params;
					const firstItem = isFirstOfRow(row);
					const fileMigrationInProgress = row.fileMigrationInProgress;

					const isFirst = rows.indexOf(row) === 0;

					const classes = [
						`row-item-id-${row.id}`,
						`${firstItem && !isFirst ? ' first-row-of-item' : ''}`,
						`${hoveredRowId === row.id ? ' hovered-item-row' : ''}`,
						`${fileMigrationInProgress ? ' file-migration-in-progress' : ''}`,
					]
						.filter(Boolean)
						.join(' ');

					return classes;
				}}
				// autoHeight
				width='100%'
				getRowHeight={() => 'auto'}
				columns={gridColumns}
				rows={rows}
				// showCellVerticalBorder
				// showColumnVerticalBorder
				disableRowSelectionOnClick
				onRowClick={handleRowClick}
				{...(checkboxSelection && {
					checkboxSelection: true,
					rowSelectionModel,
					onRowSelectionModelChange: handleRowSelectionModelChange,
					keepNonExistentRowsSelected: true,
					isRowSelectable,
				})}
				// rowSpanning
				disableSelectionOnClick
				disableColumnFilter
				columnVisibilityModel={columnVisibilityModel}
				onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
				onColumnWidthChange={handleColumnWidthChange}
				sortingMode='server'
				sortModel={sortModel}
				onSortModelChange={setSortModel}
				// disableColumnSelector
				// disableColumnMenu
				// hideFooterPagination
				// hideFooter
				slots={{
					row: LinkedGridRow,
					noRowsOverlay: InstantSearchDataGridNoRowsOverlay,
					pagination: InstantSearchDataGridPagination,
					toolbar: InstantSearchDataGridToolbar,
					columnMenu: DataGridColumnMenu,
				}}
				slotProps={{
					loadingOverlay: {
						variant: 'skeleton',
						noRowsVariant: 'skeleton',
					},
					row: {
						onAuxClick: handleRowAuxClick,
						onMouseEnter: (event) => {
							const rowId = event.currentTarget.getAttribute('data-id');
							const row = rows.find((r) => r.uniqueId === rowId);
							if (row) {
								setHoveredRowId(row.id);
							}
						},
						onMouseLeave: (event) => {
							if (
								event.relatedTarget instanceof Node &&
								event.currentTarget.contains(event.relatedTarget)
							) {
								return;
							}
							setHoveredRowId(null);
						},
					},
				}}
			/>
			<LoadingDataGridRowOverlay rowElement={loadingRowElement} />
		</Box>
	);
};

InstantSearchDataGridWrapper.displayName = 'InstantSearchDataGridWrapper';

export default InstantSearchDataGrid;
